import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { LANDING } from "@/constants/testIds";

const WEBHOOK_URL =
  process.env.NODE_ENV === "development"
    ? "https://agencia-firma-n8n.ew0aju.easypanel.host/webhook-test/ce253e88-975c-4a8a-9367-fbe1317a9be0"
    : "https://agencia-firma-n8n.ew0aju.easypanel.host/webhook/ce253e88-975c-4a8a-9367-fbe1317a9be0";

function formatWhatsapp(value) {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

export default function LeadDialog({ open, onOpenChange, triggerTestId }) {
  const [form, setForm] = useState({ name: "", email: "", whatsapp: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const update = (field) => (e) => {
    const value = field === "whatsapp" ? formatWhatsapp(e.target.value) : e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const validate = () => {
    if (form.name.trim().length < 2) return "Por favor informe seu nome completo.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Informe um e-mail válido.";
    const digits = form.whatsapp.replace(/\D/g, "");
    if (digits.length < 11) return "Informe um WhatsApp válido com DDD + 9 dígitos.";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await axios.post(WEBHOOK_URL, {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        whatsapp: form.whatsapp.trim(),
        source: "landing-china",
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      toast.success("Recebemos seus dados! Redirecionando...");
      // Persist name for thank-you personalization (kept for compatibility)
      try {
        sessionStorage.setItem("lead_name", form.name.trim().split(" ")[0]);
      } catch (_) { /* ignore */ }
      // External redirect to Kiwify checkout
      setTimeout(() => {
        window.location.href = "https://pay.kiwify.com.br/GkFxBA5";
      }, 600);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Não foi possível enviar agora. Tente novamente.");
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogContent
        data-testid={LANDING.leadDialog}
        withOverlay={false}
        className="sm:max-w-[480px] bg-[var(--brand-cream)] border-[var(--brand-line)] rounded-2xl p-0 overflow-hidden shadow-2xl"
      >
        <div className="bg-[var(--brand-coral)] px-7 py-5">
          <DialogHeader>
            <DialogTitle className="text-white font-serif-brand text-2xl md:text-3xl font-semibold leading-tight">
              Quase lá!
            </DialogTitle>
            <DialogDescription className="text-white/90 font-sans-brand text-sm leading-relaxed">
              Preencha seus dados para receber o passo a passo completo da viagem à China direto no seu WhatsApp.
            </DialogDescription>
          </DialogHeader>
        </div>

        <form onSubmit={handleSubmit} noValidate className="px-7 py-6 space-y-4">
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-ink-soft mb-2 font-sans-brand">
              Nome
            </label>
            <input
              data-testid={LANDING.inputName}
              type="text"
              autoComplete="name"
              required
              className="brand-input"
              placeholder="Seu nome completo"
              value={form.name}
              onChange={update("name")}
            />
          </div>

          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-ink-soft mb-2 font-sans-brand">
              E-mail
            </label>
            <input
              data-testid={LANDING.inputEmail}
              type="email"
              autoComplete="email"
              required
              className="brand-input"
              placeholder="voce@email.com"
              value={form.email}
              onChange={update("email")}
            />
          </div>

          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-ink-soft mb-2 font-sans-brand">
              WhatsApp
            </label>
            <input
              data-testid={LANDING.inputWhatsapp}
              type="tel"
              autoComplete="tel"
              required
              className="brand-input"
              placeholder="(11) 99999-9999"
              value={form.whatsapp}
              onChange={update("whatsapp")}
            />
          </div>

          {error && (
            <p
              data-testid={LANDING.formError}
              className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2"
            >
              {error}
            </p>
          )}

          <button
            data-testid={LANDING.submitForm}
            type="submit"
            disabled={submitting}
            className="cta-pill w-full font-sans-brand disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {submitting ? "Enviando..." : "Quero meu acesso agora →"}
          </button>

          <p className="text-[11px] text-ink-soft text-center font-sans-brand">
            Seus dados estão seguros. Não compartilhamos com terceiros.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
